export enum DeliveryProofState {
  DRAFT = 'draft',
  PENDING_SIGNATURE = 'pending_signature',
  PENDING_UPLOAD = 'pending_upload',
  SENT = 'sent',
  SIGNED = 'signed',
  CANCELED = 'canceled',
}

export enum DeliveryProofElementType {
  MOT = 'mot',
  CARD = 'card',
  KEY = 'key',
  NUT = 'nut',
  RECORD = 'record',
  PROOF = 'proof',
  WHEEL = 'wheel',
  CUSTOM = 'custom',
  DIAGNOSTIC = 'diagnostic',
  HISTOVEC = 'histovec',
}

export enum DeliveryProofVehicleInfoType {
  KM = 'km',
  PRESENTATION = 'presentation',
  OBSERVATION = 'observation',
}

export interface BeneficiaryResponseDto {
  id: string;
  civility: string;
  firstname: string;
  lastname: string;
  societyName?: string;
  address: string;
  postalCode: string;
  city: string;
  email: string;
  phone: string;
}

export interface VehicleResponseDto {
  id: string;
  registration: string;
  brand: string;
  model: string;
  eis?: string;
  kmStart: number;
  deliveryDate: string;
  vendorType: number;
}

export interface WarrantyResponseDto {
  id: string;
  warrantyId: number | null;
  isAccepted: boolean;
  hidePrices: boolean;
  isExternal?: boolean;
  externalName?: string;
  externalKmLimit?: string;
  externalFinancialCap?: string;
  externalDuration?: string;
  externalPieces?: string;
  externalPrice?: string;
}

export interface ElementResponseDto {
  id: string;
  type: DeliveryProofElementType;
  label?: string;
  date?: string;
  /** Précision libre (emplacement si type=nut) */
  note?: string;
  fileUrl?: string;
  /** Pages JPEG dans l'ordre, page 1 incluse. Absent : une seule page (fileUrl) */
  filePages?: string[];
  /** PDF d'origine (absent pour une image) */
  originalFileUrl?: string;
}

export interface VehicleInfoResponseDto {
  id: string;
  type: DeliveryProofVehicleInfoType;
  value?: string;
  fileUrl?: string;
  /** Pages JPEG dans l'ordre, page 1 incluse. Absent : une seule page (fileUrl) */
  filePages?: string[];
  /** PDF d'origine (absent pour une image) */
  originalFileUrl?: string;
  orderIndex: number;
}

/**
 * Pièce jointe renvoyée par l'upload : `fileUrl` est la page 1 d'un PDF, ou
 * l'image ; `filePages` liste toutes les pages, page 1 incluse.
 */
export interface DeliveryProofAttachment {
  fileUrl: string;
  filePages?: string[];
  originalFileUrl?: string;
}

export interface DeliveryProofResponseDto {
  id: string;
  contractNum: string | null;
  contractId?: number;
  draftUuid: string | null;
  dpNumber?: string;
  societyId?: string;
  state: DeliveryProofState;
  signatureType?: 1 | 2;
  currentStep?: number;
  pdfUrl?: string;
  createdAt: string;
  updatedAt: string;
  sentAt?: string;
  isLegacy?: boolean;
  societyName?: string;
  creatorName?: string;
  beneficiary?: BeneficiaryResponseDto;
  vehicle?: VehicleResponseDto;
  warranties: WarrantyResponseDto[];
  elements: ElementResponseDto[];
  vehicleInfo: VehicleInfoResponseDto[];
}

export interface CreateDeliveryProofDto {
  contractNum?: string;
  draftUuid?: string;
  dpNumber?: string;
  societyId?: string;
  userId?: string;
}

export interface WarrantyItemDto {
  warrantyId?: number;
  isAccepted: boolean;
  isExternal?: boolean;
  externalName?: string;
  externalKmLimit?: string;
  externalFinancialCap?: string;
  externalDuration?: string;
  externalPieces?: string;
  externalPrice?: string;
}

export interface UpdateWarrantiesDto {
  warranties: WarrantyItemDto[];
  hidePrices: boolean;
  currentStep?: number;
}

export interface ElementItemDto {
  type: DeliveryProofElementType;
  label?: string;
  date?: string;
  /** Précision libre (emplacement si type=nut) */
  note?: string;
  fileUrl?: string;
  /** Pages JPEG dans l'ordre, page 1 incluse. Absent : une seule page (fileUrl) */
  filePages?: string[];
  /** PDF d'origine (absent pour une image) */
  originalFileUrl?: string;
}

export interface UpdateElementsDto {
  elements: ElementItemDto[];
  currentStep?: number;
}

export interface VehicleInfoItemDto {
  type: DeliveryProofVehicleInfoType;
  value?: string;
  fileUrl?: string;
  /** Pages JPEG dans l'ordre, page 1 incluse. Absent : une seule page (fileUrl) */
  filePages?: string[];
  /** PDF d'origine (absent pour une image) */
  originalFileUrl?: string;
  orderIndex?: number;
}

export interface UpdateVehicleInfoDto {
  vehicleInfo: VehicleInfoItemDto[];
  currentStep?: number;
}

export interface SendDeliveryProofDto {
  signatureType: 1 | 2;
  email?: string;
  phone?: string;
}

export interface ListDeliveryProofsDto {
  page?: number;
  limit?: number;
  state?: DeliveryProofState;
  excludeState?: DeliveryProofState;
  search?: string;
  societyId?: string;
  excludeLegacy?: boolean;
}

export interface ListDeliveryProofsResponse {
  data: DeliveryProofResponseDto[];
  total: number;
  page: number;
  limit: number;
}
